import '@fortawesome/fontawesome-svg-core/styles.css';
import { config } from '@fortawesome/fontawesome-svg-core';
config.autoAddCss = false;

import '../styles/globals.css';
import Head from 'next/head';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import Script from 'next/script';
import CookieBanner from '../components/CookieBanner';
import MyChat from '../components/MyChat';
import EngagementPopup from '../components/EngagementPopup';

import { Barlow_Condensed, Gilda_Display } from 'next/font/google';

const RECAPTCHA_SITE_KEY = '6LfHUj8rAAAAANSxaane7wlxlcpnA_Mw_QAjpUCx';

const barlow = Barlow_Condensed({
  subsets: ['latin'],
  weight: ['300', '400'],
  variable: '--font-sans',
  display: 'swap',
});

const gilda = Gilda_Display({
  subsets: ['latin'],
  weight: '400',
  variable: '--font-gilda',
  display: 'swap',
});

export default function App({ Component, pageProps }) {
  // GA lazy loader
  useEffect(() => {
    const loadGA = () => {
      const script1 = document.createElement('script');
      script1.src = 'https://www.googletagmanager.com/gtag/js?id=G-0Z1Y34H2G8';
      script1.async = true;
      document.head.appendChild(script1);

      const script2 = document.createElement('script');
      script2.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'G-0Z1Y34H2G8');
      `;
      document.head.appendChild(script2);
    };

    const onInteraction = () => {
      loadGA();
      window.removeEventListener('scroll', onInteraction);
      window.removeEventListener('click', onInteraction);
    };

    window.addEventListener('scroll', onInteraction);
    window.addEventListener('click', onInteraction);

    return () => {
      window.removeEventListener('scroll', onInteraction);
      window.removeEventListener('click', onInteraction);
    };
  }, []);

  // Anti plugin protection
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const suspiciousTexts = [
      '加为客户',       // Add as customer
      '客户代码',       // Customer code
      '业务人员代码',   // Salesperson code
      '客户名称',       // Customer name
      '客户类型',       // Customer type
      '国家/地区',      // Country or region
    ];

    const scanNode = (node) => {
      if (!(node instanceof HTMLElement)) return;
      const text = node.innerText || '';
      const matched = suspiciousTexts.some((t) => text.includes(t));
      if (!matched) return;

      // Any injected CRM text, always block
      window.location.href = '/blocked';
    };

    // Observe DOM changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          scanNode(node);
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Initial scan in case something was already injected
    scanNode(document.body);

    return () => observer.disconnect();
  }, []);

  const router = useRouter();

  useEffect(() => {
    const trackVisitor = (url) => {
      fetch('/api/track-visitor', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      }).catch(() => {});
    };

    // Track first load
    trackVisitor(window.location.pathname);

    // Track route changes
    router.events.on('routeChangeComplete', trackVisitor);

    return () => {
      router.events.off('routeChangeComplete', trackVisitor);
    };
  }, [router.events]);

  const serviceTitle =
    pageProps?.serviceTitle || pageProps?.serviceData?.title || '';

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Head>

      <Script
        id="recaptcha-v3"
        src={`https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`}
        strategy="lazyOnload"
      />

      <main className={`${barlow.variable} ${gilda.variable} font-sans`}>
        <Component {...pageProps} />
        <EngagementPopup serviceTitle={serviceTitle} />
        <CookieBanner />
        <MyChat />
      </main>
    </>
  );
}
